import { TimeUnit } from '../types';
import { getQuoteBook, getResolutionMode, resolveQuoteValue } from './quoteBook';

export type CreditCurveSource = 'flat' | 'manual' | 'quote_book';

export interface CreditCurvePoint {
  tenor_number: number;
  tenor_time_unit: TimeUnit;
  spread?: number;
  quote_id?: string;
}

export interface CreditCurveSpec {
  id: string;
  name?: string;
  reference_entity?: string;
  currency?: string;
  seniority?: string;
  source: CreditCurveSource;
  recovery_rate: number;
  flat_hazard_rate?: number;
  points?: CreditCurvePoint[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ResolvedCreditCurve {
  recovery_rate: number;
  flat_hazard_rate?: number;
  quotes?: Array<{
    tenor_number: number;
    tenor_time_unit: TimeUnit;
    quote_type: 'ParSpread';
    quote_id?: string;
    quoted_par_spread: number;
  }>;
}

const CREDIT_CURVES_KEY = 'quantra_credit_curves';

function loadStored(): CreditCurveSpec[] {
  try {
    const raw = localStorage.getItem(CREDIT_CURVES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveStored(items: CreditCurveSpec[]) {
  localStorage.setItem(CREDIT_CURVES_KEY, JSON.stringify(items));
}

export function getCreditCurves(): CreditCurveSpec[] {
  return loadStored();
}

export function getCreditCurveById(id: string): CreditCurveSpec | null {
  return getCreditCurves().find(c => c.id === id) || null;
}

export function saveCreditCurve(curve: CreditCurveSpec): void {
  const now = new Date().toISOString();
  const next: CreditCurveSpec = {
    ...curve,
    createdAt: curve.createdAt || now,
    updatedAt: now,
  };
  const items = loadStored();
  const idx = items.findIndex(c => c.id === next.id);
  if (idx >= 0) items[idx] = next;
  else items.push(next);
  saveStored(items);
}

export function deleteCreditCurve(id: string): void {
  saveStored(loadStored().filter(c => c.id !== id));
}

export async function clearCreditCurves(): Promise<void> {
  localStorage.removeItem(CREDIT_CURVES_KEY);
}

export async function replaceCreditCurves(items: CreditCurveSpec[]): Promise<void> {
  saveStored(items);
}

export function exportCreditCurves(items: CreditCurveSpec[]) {
  const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `credit-curves-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function importCreditCurves(file: File): Promise<CreditCurveSpec[]> {
  const text = await file.text();
  const data = JSON.parse(text);
  const items: CreditCurveSpec[] = Array.isArray(data) ? data : [];
  if (items.length === 0) throw new Error('No valid credit curves found in file');
  saveStored(items);
  return items;
}

export function resolveCreditCurve(spec: CreditCurveSpec, asOfDate: string): ResolvedCreditCurve {
  const recovery_rate = spec.recovery_rate;
  if (spec.source === 'flat') {
    return {
      recovery_rate,
      flat_hazard_rate: spec.flat_hazard_rate ?? 0.0,
    };
  }

  const points = spec.points || [];
  if (points.length === 0) {
    return { recovery_rate, quotes: [] };
  }

  const mode = getResolutionMode();
  const byId = new Map(getQuoteBook().map(e => [e.id, e]));

  const quotes = points
    .map(p => {
      let spread = p.spread;
      if (spec.source === 'quote_book' && p.quote_id) {
        const entry = byId.get(p.quote_id);
        if (entry) {
          const resolved = resolveQuoteValue(entry.series, asOfDate, mode);
          if (resolved !== null) spread = resolved;
        }
      }
      if (spread === undefined || spread === null || Number.isNaN(spread)) return null;
      return {
        tenor_number: p.tenor_number,
        tenor_time_unit: p.tenor_time_unit,
        quote_type: 'ParSpread' as const,
        ...(p.quote_id ? { quote_id: p.quote_id } : {}),
        quoted_par_spread: spread,
      };
    })
    .filter((v): v is {
      tenor_number: number;
      tenor_time_unit: TimeUnit;
      quote_type: 'ParSpread';
      quote_id?: string;
      quoted_par_spread: number;
    } => v !== null);

  return { recovery_rate, quotes };
}
